<script>
import { ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  ALLOWED_FILE_TYPES,
  ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP,
  ALLOWED_FILE_TYPES_FOR_LINE,
} from 'shared/constants/messages';
import VideoCallButton from '../VideoCallButton.vue';
import AIAssistanceButton from '../AIAssistanceButton.vue';
import { REPLY_EDITOR_MODES } from './constants';
import { mapGetters } from 'vuex';


const DEFAULT_BUTTON_CONFIG = {
  emojiPicker: true,
  attachment: true,
  audioRecorder: true,
  editor: true,
  signature: true,
  whatsappTemplates: true,
  videoCall: true,
  aiAssistant: true,
  articleSearch: true,
};

export default {
  name: 'ReplyBottomPanel',
  components: { 
    FileUpload, 
    VideoCallButton, 
    AIAssistanceButton 
  },
  mixins: [inboxMixin],
  
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '00:00',
    },
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: Boolean,
    showAudioRecorder: Boolean,
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: Boolean,
    recordingAudioState: String,
    isSendDisabled: Boolean,
    showEditorToggle: Boolean,
    isOnPrivateNote: Boolean,
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    hasWhatsappTemplates: Boolean,
    conversationId: {
      type: Number,
      required: true,
    },
    message: String,
    newConversationModalActive: Boolean,
    portalSlug: String,
    // Simplified button configuration
    buttonConfig: {
      type: Object,
      default: () => DEFAULT_BUTTON_CONFIG,
    },
  },
  
  emits: [
    'replaceText',
    'toggleInsertArticle',
    'toggleEditor',
    'selectWhatsappTemplate',
  ],
  
  setup() {
    const { setSignatureFlagForInbox, fetchSignatureFlagFromUISettings, isEditorHotKeyEnabled } = useUISettings();
    const uploadRef = ref(null);

    // Simplified keyboard shortcuts
    const keyboardEvents = {
      'Alt+KeyA': {
        action: () => document.querySelector('#conversationAttachment')?.click(),
        allowOnFocusedInput: true,
      },
    };

    useKeyboardEvents(keyboardEvents);

    return {
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
      isEditorHotKeyEnabled,
      uploadRef,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'integrations/getUIFlags',
    }),
    isNote() {
      return this.mode === REPLY_EDITOR_MODES.NOTE;
    },

    mergedButtonConfig() {
      return { ...DEFAULT_BUTTON_CONFIG, ...this.buttonConfig };
    },

    wrapClass() {
      return { 'is-note-mode': this.isNote };
    },
    buttonClass() {
      return { warning: this.isNote };
    },

    allButtons() {
      const buttons = [];

      if (this.mergedButtonConfig.emojiPicker) {
        buttons.push(this.createButton('emoji', {
          icon: 'emoji',
          tooltip: 'CONVERSATION.REPLYBOX.TIP_EMOJI_ICON',
          action: this.toggleEmojiPicker,
        }));
      }

      if (this.mergedButtonConfig.attachment && this.showAttachButton) {
        buttons.push(this.createButton('attachment', {
          component: 'FileUpload',
          icon: 'attach',
          tooltip: 'CONVERSATION.REPLYBOX.TIP_ATTACH_ICON',
          action: this.onFileUpload,
        }));
      }

      if (this.mergedButtonConfig.audioRecorder && this.canShowAudioRecorder) {
        buttons.push(this.createButton('audio', {
          icon: this.isRecordingAudio ? 'microphone-off' : 'microphone',
          colorScheme: this.isRecordingAudio ? 'alert' : 'secondary',
          tooltip: 'CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON',
          action: this.toggleAudioRecorder,
        }));
      }

      if (this.mergedButtonConfig.editor && this.showEditorToggle) {
        buttons.push(this.createButton('editor', {
          icon: 'quote',
          tooltip: 'CONVERSATION.REPLYBOX.TIP_FORMAT_ICON',
          action: () => this.$emit('toggleEditor'),
        }));
      }

      if (this.showAudioPlayStopButton) {
        buttons.push(this.createButton('audio-play', {
          icon: this.audioRecorderPlayStopIcon,
          action: this.toggleAudioRecorderPlayPause,
          text: this.recordingAudioDurationText,
        }));
      }

      if (this.mergedButtonConfig.signature && !this.isOnPrivateNote) {
        buttons.push(this.createButton('signature', {
          icon: 'signature',
          tooltip: this.signatureToggleTooltip,
          action: this.toggleMessageSignature,
        }));
      }

      if (this.mergedButtonConfig.whatsappTemplates && this.hasWhatsappTemplates) {
        buttons.push(this.createButton('whatsapp', {
          icon: 'whatsapp',
          tooltip: 'CONVERSATION.FOOTER.WHATSAPP_TEMPLATES',
          action: () => this.$emit('selectWhatsappTemplate'),
        }));
      }

      if (this.mergedButtonConfig.videoCall && this.canShowVideoCall) {
        buttons.push(this.createButton('video', {
          component: 'VideoCallButton',
          props: { conversationId: this.conversationId },
        }));
      }

      if (this.mergedButtonConfig.aiAssistant && !this.uiFlags.isFetching) {
        buttons.push(this.createButton('ai', {
          component: 'AIAssistanceButton',
          props: {
            conversationId: this.conversationId,
            isPrivateNote: this.isOnPrivateNote,
            message: this.message,
          },
          events: { 'replace-text': this.replaceText },
        }));
      }

      if (this.mergedButtonConfig.articleSearch && this.portalSlug) {
        buttons.push(this.createButton('article', {
          icon: 'document-text-link',
          tooltip: 'HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH',
          action: this.toggleInsertArticle,
        }));
      }

      return buttons;
    },
    showAttachButton() {
      return this.showFileUpload || this.isNote;
    },

    canShowAudioRecorder() {
      return !this.isALineChannel && 
             this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.VOICE_RECORDER) && 
             this.showAudioRecorder;
    },
    showAudioPlayStopButton() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    canShowVideoCall() {
      return (this.isAWebWidgetInbox || this.isAPIInbox) && !this.isOnPrivateNote;
    },
    allowedFileTypes() {
      if (this.isATwilioWhatsAppChannel) return ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP;
      if (this.isALineChannel) return ALLOWED_FILE_TYPES_FOR_LINE;
      return ALLOWED_FILE_TYPES;
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    audioRecorderPlayStopIcon() {
      const iconMap = {
        playing: 'microphone-pause',
        paused: 'microphone-play',
        stopped: 'microphone-play',
      };
      return iconMap[this.recordingAudioState] || 'microphone-stop';
    },

    sendWithSignature() {
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },

    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },

    keyboardShortcutHint() {
      const isEnterToSend = this.isEditorHotKeyEnabled('enter');
      return isEnterToSend 
        ? this.isOnPrivateNote ? this.$t('CONVERSATION.FOOTER.KEYBOARD_HINT_ENTER_TO_SEND_PRIVATE') : this.$t('CONVERSATION.FOOTER.KEYBOARD_HINT_ENTER_TO_SEND')
        : this.$t('CONVERSATION.FOOTER.KEYBOARD_HINT_CTRL_ENTER_TO_SEND');
    },
  },

  mounted() {
    ActiveStorage.start();
  },
  methods: {
    createButton(id, config) {
      return {
        id,
        ...config,
      };
    },

    toggleMessageSignature() {
      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
    replaceText(text) {
      this.$emit('replaceText', text);
    },
    toggleInsertArticle() {
      this.$emit('toggleInsertArticle');
    },
    renderButton(button) {
      if (button.component === 'FileUpload') {
        return this.renderFileUpload(button);
      }
      
      if (button.component) {
        return this.renderSpecialComponent(button);
      }
      
      return this.renderRegularButton(button);
    },
  },
};
</script>

<template>
  <div class="bottom-box" :class="wrapClass">
    <div class="max-w-[70%] ">
      <p class="text-n-gray-6 text-xs whitespace-normal break-words">{{ keyboardShortcutHint }}</p>
    </div>
    <div class="left-wrap">
      <template v-for="button in allButtons" :key="button.id">
        <button
          v-if="!button.component"
          v-tooltip.top-end="button.tooltip ? $t(button.tooltip) : ''"
          :title="button.tooltip ? $t(button.tooltip) : ''"
          class="flex items-center justify-center p-0 rounded-md bg-transparent dark:text-white"
          @click="button.action"
        >
          <fluent-icon v-if="button.icon" :icon="button.icon" size="16" />
          <span v-if="button.text" class="ml-1 text-sm">{{ button.text }}</span>
        </button>

        <!-- File Upload Component -->
        <FileUpload
          v-else-if="button.component === 'FileUpload'"
          ref="uploadRef"
          v-tooltip.top-end="$t(button.tooltip)"
          input-id="conversationAttachment"
          :size="4096 * 4096"
          :accept="allowedFileTypes"
          :multiple="enableMultipleFileUpload"
          :drop="enableDragAndDrop"
          :drop-directory="false"
          :data="{
            direct_upload_url: '/rails/active_storage/direct_uploads',
            direct_upload: true,
          }"
          @input-file="button.action"
        >
          <button
            class="button--upload flex items-center justify-center p-0 rounded-md bg-transparent"
            :title="$t(button.tooltip)"
          >
            <fluent-icon v-if="button.icon" :icon="button.icon" size="16" />
          </button>
        </FileUpload>

        <VideoCallButton
          v-else-if="button.component === 'VideoCallButton'"
          :conversation-id="button.props.conversationId"
        />
        
        <AIAssistanceButton
          v-else-if="button.component === 'AIAssistanceButton'"
          v-bind="button.props"
          @replace-text="button.events['replace-text']"
        />
      </template>

      <!-- Drag Drop Overlay -->
      <Transition name="modal-fade">
        <div
          v-show="uploadRef?.dropActive"
          class="fixed inset-0 z-20 flex flex-col items-center justify-center gap-2 text-slate-900 dark:text-slate-50 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
        >
          <fluent-icon icon="cloud-backup" size="40" />
          <h4 class="text-2xl text-slate-900 dark:text-slate-50">
            {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
          </h4>
        </div>
      </Transition>
    </div>
    <!-- <div class="right-wrap">
      <woot-button
        size="small"
        :class-names="buttonClass"
        :is-disabled="isSendDisabled"
        @click="onSend"
      >
        {{ sendButtonText }}
      </woot-button>
    </div> -->
  </div>
</template>

<style lang="scss" scoped>
.bottom-box {
  @apply flex justify-between py-3 px-3 md:px-4;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 1rem; // px-4 = 1rem
    right: 1rem; // px-4 = 1rem
    height: 1px;
    @apply border-t border-n-gray-2 dark:border-n-vars-black-bd;
  }

  &.is-note-mode {
    @apply bg-yellow-50 dark:bg-yellow-800 rounded-b-md overflow-hidden;
  }
}

.left-wrap {
  @apply flex items-center gap-3 md:gap-4 flex-wrap;
}

.right-wrap {
  @apply flex;
}

:deep(.file-uploads) {
  label {
    @apply cursor-pointer;
  }

  &:hover button {
    @apply bg-slate-100 dark:bg-slate-800;
  }
}
</style>